// src/components/projects/Project.js
import React from 'react';
import styles from '../../styles/components/Project.module.css';

/**
 * Project card component to display individual project details
 * 
 * @param {Object} props - Component props
 * @param {string} [props.id] - Element ID for anchor linking
 * @param {string} props.title - Project title
 * @param {string} props.description - Project description
 * @param {string[]} props.technologies - Array of technologies used
 * @param {string} [props.imageUrl] - URL to project image (optional)
 * @param {string} [props.githubUrl] - URL to GitHub repository
 * @param {string} [props.liveUrl] - URL to live demo
 */
export function Project({
  id,
  title,
  description,
  technologies = [],
  imageUrl,
  githubUrl,
  liveUrl
}) {
  // Use a different class when there's no image to allow content to fill the card
  const projectCardClass = imageUrl 
    ? styles.projectCard 
    : `${styles.projectCard} ${styles.projectCardNoImage}`;

  return (
    <div id={id} className={projectCardClass}>
      {/* Only render the image container if an image URL is provided */}
      {imageUrl && (
        <div className={styles.imageContainer}>
          <img 
            src={imageUrl} 
            alt={title} 
            className={styles.projectImage}
            onError={(e) => {
              e.target.onerror = null; 
              e.target.style.display = 'none';
              e.target.parentNode.classList.add(styles.fallbackImageContainer);
            }} 
          />
          <div className={styles.overlay}></div>
        </div>
      )}
      
      <div className={styles.projectContent}>
        <h3 className={styles.projectTitle}>{title}</h3>
        <p className={styles.projectDescription}>{description}</p>
        
        {technologies.length > 0 && (
          <div className={styles.technologies}>
            <h4>Technologies:</h4>
            <div className={styles.techList}>
              {technologies.map((tech, index) => (
                <span key={index} className={styles.techTag}>
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className={styles.projectLinks}>
          {githubUrl && (
            <a 
              href={githubUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.projectLink}
            >
              GitHub
            </a>
          )}
          {liveUrl && (
            <a 
              href={liveUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.projectLinkAlt}
            >
              Live Demo
            </a>
          )}
        </div>
      </div>
    </div>
  );
}